import React, { useState, useMemo } from "react";
import "./StatusView.css";
import { MOCK_REPAIR_RECORDS, RepairRecord, RepairStatus, formatCost, formatTime } from "../repairData";

type FilterStatus = "전체" | RepairStatus;

const FILTER_OPTIONS: FilterStatus[] = ["전체", "대기중", "진행중", "완료", "취소"];

const STATUS_DOT_COLOR: Record<RepairStatus, string> = {
    "대기중": "#9ca3af",
    "진행중": "#0ea5e9",
    "완료": "#10b981",
    "취소": "#ef4444",
};

export default function StatusView() {
    const [filterStatus, setFilterStatus] = useState<FilterStatus>("전체");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedRecord, setSelectedRecord] = useState<RepairRecord | null>(null);
    const [page, setPage] = useState(0);
    const rowsPerPage = 8;

    const filtered = useMemo(() => {
        let data = [...MOCK_REPAIR_RECORDS];
        if (filterStatus !== "전체") {
            data = data.filter(r => r.status === filterStatus);
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            data = data.filter(r =>
                r.vehicleNumber.toLowerCase().includes(q) ||
                r.faultName.toLowerCase().includes(q) ||
                r.shopName.toLowerCase().includes(q) ||
                r.driverName.toLowerCase().includes(q)
            );
        }
        return data;
    }, [filterStatus, searchQuery]);

    const paged = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    const totalPages = Math.ceil(filtered.length / rowsPerPage);

    const counts = useMemo(() => ({
        total: MOCK_REPAIR_RECORDS.length,
        waiting: MOCK_REPAIR_RECORDS.filter(r => r.status === "대기중").length,
        active: MOCK_REPAIR_RECORDS.filter(r => r.status === "진행중").length,
        done: MOCK_REPAIR_RECORDS.filter(r => r.status === "완료").length,
    }), []);

    return (
        <div className="status-page">
            <div className="status-header">
                <h2 className="status-title">정비 현황</h2>
                <p className="status-subtitle">차량별 수리 요청 현황 및 진행 상태를 실시간으로 확인합니다</p>
            </div>

            {/* Summary cards */}
            <div className="status-summary-row">
                <div className="summary-card">
                    <div className="summary-card-icon total">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                        </svg>
                    </div>
                    <div className="summary-card-text">
                        <span className="summary-card-value">{counts.total}</span>
                        <span className="summary-card-label">전체 요청</span>
                    </div>
                </div>
                <div className="summary-card">
                    <div className="summary-card-icon waiting">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                        </svg>
                    </div>
                    <div className="summary-card-text">
                        <span className="summary-card-value">{counts.waiting}</span>
                        <span className="summary-card-label">대기 중</span>
                    </div>
                </div>
                <div className="summary-card">
                    <div className="summary-card-icon active">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                        </svg>
                    </div>
                    <div className="summary-card-text">
                        <span className="summary-card-value">{counts.active}</span>
                        <span className="summary-card-label">정비 중</span>
                    </div>
                </div>
                <div className="summary-card">
                    <div className="summary-card-icon done">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </div>
                    <div className="summary-card-text">
                        <span className="summary-card-value">{counts.done}</span>
                        <span className="summary-card-label">완료</span>
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="status-toolbar">
                <div className="status-filter-chips">
                    {FILTER_OPTIONS.map(f => (
                        <button
                            key={f}
                            className={`filter-chip ${filterStatus === f ? "active" : ""}`}
                            onClick={() => { setFilterStatus(f); setPage(0); }}
                        >
                            {f}
                        </button>
                    ))}
                </div>
                <div className="status-search-wrap">
                    <svg className="status-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        className="status-search-input"
                        placeholder="차량번호, 정비소, 운전자 검색"
                        value={searchQuery}
                        onChange={e => { setSearchQuery(e.target.value); setPage(0); }}
                    />
                </div>
            </div>

            {/* Main content */}
            <div className="status-main">
                <div className="status-table-card">
                    <div className="status-table-wrap">
                        <table className="status-table">
                            <thead>
                                <tr>
                                    <th>차량 정보</th>
                                    <th>고장 유형</th>
                                    <th>심각도</th>
                                    <th>정비소</th>
                                    <th>요청 시각</th>
                                    <th>예상 시간</th>
                                    <th>예상 비용</th>
                                    <th>상태</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} style={{ textAlign: "center", color: "#9ca3af", padding: "40px 20px" }}>
                                            검색 결과가 없습니다
                                        </td>
                                    </tr>
                                ) : paged.map(record => (
                                    <tr
                                        key={record.id}
                                        className={selectedRecord?.id === record.id ? "row-selected" : ""}
                                        onClick={() => setSelectedRecord(selectedRecord?.id === record.id ? null : record)}
                                    >
                                        <td>
                                            <div className="vehicle-cell">
                                                <span className="vehicle-cell-number">{record.vehicleNumber}</span>
                                                <span className="vehicle-cell-model">{record.vehicleModel}</span>
                                            </div>
                                        </td>
                                        <td style={{ fontSize: 13 }}>{record.faultName}</td>
                                        <td>
                                            <span className={`repair-severity-badge ${record.severity}`}>
                                                {record.severity}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: 13 }}>{record.shopName}</td>
                                        <td style={{ fontSize: 13, color: "#6b7280" }}>{record.requestTime}</td>
                                        <td style={{ fontSize: 13 }}>{formatTime(record.estimatedTime)}</td>
                                        <td style={{ fontSize: 13, fontWeight: 600 }}>{formatCost(record.estimatedCost)}</td>
                                        <td>
                                            <span className={`repair-status-badge ${record.status}`}>
                                                <span className="status-dot" style={{ background: STATUS_DOT_COLOR[record.status] }} />
                                                {record.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="status-pagination">
                        <span className="pagination-info">
                            총 {filtered.length}건 중 {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, filtered.length)}건
                        </span>
                        <div className="pagination-controls">
                            <button className="page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>이전</button>
                            <span className="page-indicator">{page + 1} / {Math.max(1, totalPages)}</span>
                            <button className="page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>다음</button>
                        </div>
                    </div>
                </div>

                {/* Detail Panel */}
                <div className="status-detail-panel">
                    <div className="detail-panel-header">
                        {selectedRecord ? "상세 정보" : "상세 정보"}
                        {selectedRecord && (
                            <button className="detail-close-btn" onClick={() => setSelectedRecord(null)}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        )}
                    </div>

                    {selectedRecord ? (
                        <div className="detail-panel-body">
                            {/* Status */}
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <span className={`repair-status-badge ${selectedRecord.status}`} style={{ fontSize: 13, padding: "6px 14px" }}>
                                    <span className="status-dot" style={{ background: STATUS_DOT_COLOR[selectedRecord.status] }} />
                                    {selectedRecord.status}
                                </span>
                                <span className={`repair-severity-badge ${selectedRecord.severity}`}>
                                    {selectedRecord.severity}
                                </span>
                            </div>

                            <hr className="detail-divider" />

                            {/* Vehicle info */}
                            <div className="detail-section">
                                <div className="detail-section-title">차량 정보</div>
                                <div className="detail-row">
                                    <span className="detail-row-label">차량번호</span>
                                    <span className="detail-row-value">{selectedRecord.vehicleNumber}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-row-label">모델</span>
                                    <span className="detail-row-value" style={{ fontSize: 12 }}>{selectedRecord.vehicleModel}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-row-label">운전자</span>
                                    <span className="detail-row-value">{selectedRecord.driverName}</span>
                                </div>
                            </div>

                            <hr className="detail-divider" />

                            {/* Fault info */}
                            <div className="detail-section">
                                <div className="detail-section-title">고장 정보</div>
                                <div className="detail-row">
                                    <span className="detail-row-label">경고등</span>
                                    <span className="detail-row-value">{selectedRecord.faultName}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-row-label">고장 코드</span>
                                    <span className="detail-row-value" style={{ fontFamily: "monospace", fontSize: 13 }}>{selectedRecord.faultCode}</span>
                                </div>
                            </div>

                            <hr className="detail-divider" />

                            {/* Shop info */}
                            <div className="detail-section">
                                <div className="detail-section-title">정비소 정보</div>
                                <div className="detail-row">
                                    <span className="detail-row-label">정비소명</span>
                                    <span className="detail-row-value" style={{ fontSize: 12, textAlign: "right" }}>{selectedRecord.shopName}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-row-label">연락처</span>
                                    <span className="detail-row-value" style={{ fontSize: 13 }}>{selectedRecord.shopPhone}</span>
                                </div>
                            </div>

                            <hr className="detail-divider" />

                            {/* Schedule & cost */}
                            <div className="detail-section">
                                <div className="detail-section-title">일정 및 비용</div>
                                <div className="detail-row">
                                    <span className="detail-row-label">요청 시각</span>
                                    <span className="detail-row-value" style={{ fontSize: 12 }}>{selectedRecord.requestTime}</span>
                                </div>
                                {selectedRecord.completedTime && (
                                    <div className="detail-row">
                                        <span className="detail-row-label">완료 시각</span>
                                        <span className="detail-row-value" style={{ fontSize: 12 }}>{selectedRecord.completedTime}</span>
                                    </div>
                                )}
                                <div className="detail-row">
                                    <span className="detail-row-label">예상 시간</span>
                                    <span className="detail-row-value">{formatTime(selectedRecord.estimatedTime)}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-row-label">예상 비용</span>
                                    <span className="detail-row-value" style={{ color: "#0ea5e9" }}>{formatCost(selectedRecord.estimatedCost)}</span>
                                </div>
                            </div>

                            {selectedRecord.notes && (
                                <>
                                    <hr className="detail-divider" />
                                    <div className="detail-section">
                                        <div className="detail-section-title">비고</div>
                                        <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, background: "#f9fafb", borderRadius: 6, padding: "10px 12px" }}>
                                            {selectedRecord.notes}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="detail-empty">
                            <svg className="detail-empty-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                            </svg>
                            <div>행을 클릭하면<br />상세 정보를 확인할 수 있습니다</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
